import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, BookOpen, Sparkles, Loader2, Trash2, Scale } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_QUESTIONS = [
  'ŞİB bildirimi ne zaman yapılır?',
  'Bizim kurumumuzda risk yönetim prosedürü nedir?',
  'X biriminin yöneticisinin görev tanımı nedir?',
  'PEP nedir?',
];

export default function MevzuatChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Merhaba! Hem **kurumunuzun kendi iç mevzuatından** (politika, prosedür, iş akışı, görev tanımı) hem de MASAK, TCMB, BDDK, KVKK gibi kamuya açık regülasyonlardan soru-cevap yapabilirim.\n\n*Örnek:* "Ödeme onay iş akışımızda kaç aşama var?" ya da "MASAK ŞİB süresi nedir?"',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 
        'Authorization': `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      };

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 45000);

      const response = await fetch(`${API}/api/chatbot/ask`, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          message: text.trim(),
          session_id: sessionId
        })
      });
      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: data.response,
          refs: data.refs || [],
          sources: data.sources || [],
          relatedLesson: data.related_lesson
        }]);
      } else if (response.status === 401) {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Oturumunuz sonlandi. Lutfen tekrar giris yapin.' }]);
      } else {
        throw new Error('API error');
      }
    } catch (err) {
      const isTimeout = err.name === 'AbortError';
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        text: isTimeout ? 'Yanit suresi doldu. Sorunuzu daha kisa sormay deneyin.' : 'Baglanti hatasi. Lutfen tekrar deneyin.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        const token = localStorage.getItem('auth_token');
        await fetch(`${API}/api/chatbot/history/${sessionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Clear chat error:', err);
      }
    }
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      text: 'Merhaba! MASAK, TCMB, BDDK, KVKK ve tum finansal uyum mevzuatlari hakkinda sorularinizi yanitlayabilirim.',
    }]);
  };

  const formatMessage = (text) => {
    // Güvenli render: önce HTML escape, sonra bold + satır atlamaları.
    // Aksi halde RAG çıktısındaki model halüsinasyonu veya enjekte edilmiş
    // içerik tarayıcıda XSS olarak çalışabilir.
    const escaped = String(text ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return escaped
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />');
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[100] w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl transition-all hover:scale-110 group"
          style={{ background: 'hsl(215 55% 23%)', boxShadow: '0 4px 20px rgba(27,54,93,.3)' }}
          data-testid="chatbot-btn">
          <Scale className="w-6 h-6 text-white group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-[8px] font-black text-white flex items-center justify-center shadow-lg">AI</span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[100] w-[380px] h-[520px] rounded-2xl flex flex-col overflow-hidden"
          style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', boxShadow: '0 20px 60px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.05)' }}
          data-testid="chatbot-panel">

          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-border/30 shrink-0"
            style={{ background: 'linear-gradient(135deg,rgba(124,131,255,.06),rgba(167,139,250,.06))' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c83ff,#a78bfa)' }}>
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-[13px] font-bold text-foreground">Mevzuat Asistani</div>
                <div className="text-[9px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Gemini AI - Guncel
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} 
                className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center hover:bg-muted/50"
                title="Sohbeti temizle">
                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center hover:bg-muted/50">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-[13px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-primary/20 text-foreground rounded-br-sm'
                    : 'bg-muted/30 text-foreground/90 rounded-bl-sm'
                }`}>
                  <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                  
                  {/* RAG Sources */}
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/20" data-testid="rag-sources">
                      <div className="text-[10px] text-muted-foreground/70 mb-1.5 flex items-center gap-1">
                        <BookOpen className="w-2.5 h-2.5" />Bilgi kaynakları:
                      </div>
                      <div className="space-y-1">
                        {msg.sources.map((src, j) => {
                          const isInternal = src.scope === 'internal' || src.category === 'ic_dokuman';
                          const dotColor = isInternal
                            ? 'bg-cyan-400'
                            : src.type === 'lesson' ? 'bg-emerald-400' : 'bg-amber-400';
                          const label = isInternal ? 'Kurumsal' : null;
                          const href = isInternal
                            ? `/mevzuat?category=ic_dokuman&doc=${encodeURIComponent(src.name)}`
                            : `/mevzuat?category=${encodeURIComponent(src.category || '')}&doc=${encodeURIComponent(src.name)}`;
                          return (
                            <a key={j} href={href}
                              className="flex items-center gap-1.5 text-[10px] hover:bg-muted/30 rounded px-1 py-0.5 transition-colors">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
                              <span className="text-foreground/80 truncate">{src.name}</span>
                              {label && (
                                <span className="text-[8px] px-1 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 shrink-0">
                                  {label}
                                </span>
                              )}
                              <span className="text-muted-foreground/40 ml-auto shrink-0">{Math.round(src.score * 100)}%</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* References */}
                  {msg.refs && msg.refs.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/20">
                      <div className="text-[10px] text-muted-foreground mb-1">Kaynaklar:</div>
                      <div className="flex flex-wrap gap-1">
                        {msg.refs.map((ref, j) => (
                          <span key={j} className="px-1.5 py-0.5 rounded text-[9px] bg-primary/10 text-primary">
                            {ref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Related Lesson */}
                  {msg.relatedLesson && (
                    <div className="mt-2 pt-2 border-t border-border/20">
                      <a href={msg.relatedLesson.id ? `/lesson/${msg.relatedLesson.id}` : '#'}
                        className="flex items-center gap-1.5 text-[10px] text-amber-400 hover:text-amber-300">
                        <BookOpen className="w-3 h-3" />
                        <span>Ilgili Ders: {msg.relatedLesson.name}</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="max-w-[85%] px-3 py-2 rounded-xl bg-muted/30 rounded-bl-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-[12px] text-muted-foreground">Dusunuyor...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-3 pb-2">
              <div className="text-[10px] text-muted-foreground mb-1.5">Hizli Sorular:</div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)}
                    className="px-2 py-1 rounded-lg text-[10px] bg-muted/30 hover:bg-muted/50 text-foreground/80 transition-colors"
                    disabled={loading}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-border/30 shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Mevzuat hakkinda sorun..."
                className="flex-1 px-3 py-2 rounded-xl bg-muted/30 border border-border/30 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <button type="submit" disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#7c83ff,#a78bfa)' }}
                data-testid="chatbot-send">
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
